import type { IncomingMessage, ServerResponse } from 'http';

import { getLOB, LOB_PPLUS } from './lob-utils';
import { isStaticAsset } from './prefetch-utils';

/**
 * Next's canonical error-response Cache-Control. Use it on any handcrafted 404/error
 * response — and on `notFound` page renders for URLs matched by the `headers()` rules
 * (common-headers.js), since Next preserves an already-set Cache-Control on page renders
 * and a missing hashed chunk would otherwise ship `public, max-age=…, immutable` and get
 * cached by CDNs and browsers (deploy-window cache poisoning — CBSENTWEB-4850).
 */
export const NOT_FOUND_CACHE_CONTROL = 'private, no-cache, no-store, max-age=0, must-revalidate';

/**
 * Ends the response as an uncacheable plain-text `404`. Only for responses that are NOT
 * rendered afterwards — `writeHead` marks the headers as sent, so a subsequent Next page
 * render (e.g. a `notFound: true` return) would break; use
 * `res.setHeader('Cache-Control', NOT_FOUND_CACHE_CONTROL)` there instead.
 */
export function endPlainTextNotFound(res: ServerResponse): void {
    res.writeHead(404, {
        'Cache-Control': NOT_FOUND_CACHE_CONTROL,
        'Content-Type': 'text/plain; charset=utf-8',
    });
    res.end('404');
}

/**
 * Ends 404 responses for build/static asset URLs with a plain-text body instead of a page
 * render — nothing requesting an asset can render a page, and the plain response avoids
 * the 404 page's SSG lookup and payload.
 *
 * @returns true when the response was ended here and the caller should stop.
 */
export function endNotFoundAssetResponse(
    req: IncomingMessage | undefined,
    res: ServerResponse | undefined,
): boolean {
    if (!res || res.statusCode !== 404 || res.headersSent) {
        return false;
    }

    const url = req?.url ?? '';
    const pathname = url.split('?')[0];
    if (!pathname.startsWith('/_next') && !isStaticAsset(url)) {
        return false;
    }

    endPlainTextNotFound(res);
    return true;
}

/**
 * Shared server-side 404 handling for MFE `_app.getInitialProps`:
 *
 * 1. Build/static asset 404s (all brands): plain-text uncacheable `404` via
 *    {@link endNotFoundAssetResponse} — never a page render, never a redirect.
 * 2. Page 404s on P+ (outside local/platypus): legacy 302 to the P+ error page. The
 *    target is paramountplus.com, so this is gated on LOB — Pluto must never redirect
 *    there.
 * 3. Page 404s on Pluto: untouched — the regular 404 render proceeds (the router marks
 *    it no-store).
 *
 * @returns true when the response was ended here and the caller should stop.
 */
export function handleMfeNotFound(
    req: IncomingMessage | undefined,
    res: ServerResponse | undefined,
): boolean {
    if (!res || res.statusCode !== 404 || res.headersSent) {
        return false;
    }

    if (endNotFoundAssetResponse(req, res)) {
        return true;
    }

    if (getLOB(req) !== LOB_PPLUS) {
        return false;
    }

    if (['local', 'platypus'].includes(process.env.CURRENT_ENV as string)) {
        return false;
    }

    // URL should be env.paramountplus.com, except for prod which has no prefix
    let envPrefix = '';
    if (process.env.CURRENT_ENV !== 'prod') {
        envPrefix = `${process.env.CURRENT_ENV}.`;
    }

    res.writeHead(302, {
        Location: `https://${envPrefix}paramountplus.com/error`,
    });
    res.end();
    return true;
}
